import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Navigate, Routes, Route } from "react-router-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaRedo,
  FaSave,
  FaUndo,
} from "react-icons/fa";
import PunchingPage from "../../PunchingPage";
import AccountingPage from "../../AccountingPage";
import BulletinPage from "../../BulletinPage";
import NotePage from "../../NotePage";
import TodoPage from "../../TodoPage";
import DocumentProcessingPage from "../../DocumentProcessingPage";
import QuestionBankAdminPage from "../../QuestionBankAdminPage";
import QuestionBankPickerPage from "../../QuestionBankPickerPage";
import CloudflareFileManager from "../../../components/upload/CloudflareFileManager";
import AuthThemeToggle from "./AuthThemeToggle";
import {
  calculateAnchoredScrollTop,
  selectViewportAnchor,
} from "../../../shared/scrollAnchor";

import {
  StyledNavWrapper,
  StyledNav,
  StyledLink,
  IconButtonLeft,
  IconButtonRight,
  AuthenticatedToolbar,
  CenteredContainer,
  HistoryButtonGroup,
  StyledButton,
} from "./styles";

const persistentRouteKeys = ["accounting", "clock", "board", "documents", "math-bank", "math-bank-picker", "upload"];

const getActivePersistentRoute = (pathname) => {
  const normalizedPath = pathname.replace(/\/+$/, "");
  return persistentRouteKeys.find((path) => normalizedPath === `/${path}`) || null;
};

const AuthenticatedView = ({
  noteData,
  todoData,
  accountingData,
  accountingError,
  retryAccounting,
  onAccountingSaved,
  setNoteData,
  setTodoData,
  sendAccountingData,
  fetchAllWorkHours,
  deleteWorkHourRecord,
  onSubmit,
  onHistoryJump,
  authTheme = "day",
  onToggleAuthTheme,
  location,
  navigate,
}) => {
  const navRef = useRef(null);
  const questionEditSequenceRef = useRef(0);
  const [questionEditRequest, setQuestionEditRequest] = useState(null);
  const handleQuestionEditRequestHandled = useCallback((token) => {
    setQuestionEditRequest((current) => current?.token === token ? null : current);
  }, []);
  const activePersistentRoute = getActivePersistentRoute(location.pathname);
  const persistentScrollPositionsRef = useRef({});
  const [mountedPersistentRoutes, setMountedPersistentRoutes] = useState(() =>
    persistentRouteKeys.reduce(
      (routes, path) => ({
        ...routes,
        [path]: activePersistentRoute === path,
      }),
      {},
    ),
  );

  const menuItems = [
    { path: "board", label: "Board" },
    { path: "clock", label: "Logs" },
    { path: "accounting", label: "Finance" },
    { path: "documents", label: "Documents" },
    { path: "math-bank", label: "Question Bank" },
    { path: "math-bank-picker", label: "Pick Questions" },
    { path: "upload", label: "Cloudflare Files" },
  ];

  const handleScrollLeft = () =>
    navRef.current?.scrollBy({ left: -navRef.current.offsetWidth, behavior: "smooth" });
  const handleScrollRight = () =>
    navRef.current?.scrollBy({ left: navRef.current.offsetWidth, behavior: "smooth" });

  useEffect(() => {
    if (!activePersistentRoute) return;

    setMountedPersistentRoutes((prev) =>
      prev[activePersistentRoute]
        ? prev
        : {
            ...prev,
            [activePersistentRoute]: true,
          },
    );
  }, [activePersistentRoute]);

  const activePersistentRouteMounted = Boolean(
    activePersistentRoute && mountedPersistentRoutes[activePersistentRoute],
  );

  const getWindowScrollTop = useCallback(
    () => document.scrollingElement?.scrollTop ?? window.scrollY ?? 0,
    [],
  );

  const capturePersistentScroll = useCallback(
    (route) => {
      if (!route) return;
      const scrollTop = getWindowScrollTop();
      const routeRoot = document.querySelector(
        `[data-persistent-route="${route}"]`,
      );
      if (!routeRoot || routeRoot.closest("[hidden]")) return;
      const anchor = selectViewportAnchor(
        routeRoot.querySelectorAll("[data-tab-scroll-anchor]"),
      );

      persistentScrollPositionsRef.current[route] = {
        scrollTop,
        anchorKey: anchor?.element.dataset.tabScrollAnchor || "",
        anchorOffset: anchor?.rect.top ?? null,
      };
    },
    [getWindowScrollTop],
  );

  useEffect(() => {
    if (!activePersistentRoute || !activePersistentRouteMounted) {
      return undefined;
    }
    const handleScroll = () => capturePersistentScroll(activePersistentRoute);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [
    activePersistentRoute,
    activePersistentRouteMounted,
    capturePersistentScroll,
  ]);

  useLayoutEffect(() => {
    if (!activePersistentRoute || !activePersistentRouteMounted) return undefined;

    const savedPosition = persistentScrollPositionsRef.current[
      activePersistentRoute
    ] || { scrollTop: 0 };
    const routeRoot = document.querySelector(
      `[data-persistent-route="${activePersistentRoute}"]`,
    );
    let stopped = false;
    const restoreScroll = () => {
      if (stopped) return;
      let top = savedPosition.scrollTop || 0;
      if (savedPosition.anchorKey && routeRoot) {
        const anchor = Array.from(
          routeRoot.querySelectorAll("[data-tab-scroll-anchor]"),
        ).find(
          (element) =>
            element.dataset.tabScrollAnchor === savedPosition.anchorKey,
        );
        if (anchor) {
          top = calculateAnchoredScrollTop({
            currentScrollTop: getWindowScrollTop(),
            currentAnchorTop: anchor.getBoundingClientRect().top,
            savedAnchorOffset: savedPosition.anchorOffset,
          });
        }
      }
      window.scrollTo({ top, behavior: "auto" });
    };
    const stopRestoring = () => {
      stopped = true;
    };

    restoreScroll();
    const frame = window.requestAnimationFrame(restoreScroll);
    const observer =
      savedPosition.anchorKey && routeRoot && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(restoreScroll)
        : null;
    observer?.observe(routeRoot);
    routeRoot?.addEventListener("load", restoreScroll, true);
    window.addEventListener("wheel", stopRestoring, { passive: true });
    window.addEventListener("touchstart", stopRestoring, { passive: true });
    window.addEventListener("pointerdown", stopRestoring, { passive: true });
    window.addEventListener("keydown", stopRestoring);
    const stopTimer = window.setTimeout(stopRestoring, 3000);

    return () => {
      stopped = true;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(stopTimer);
      observer?.disconnect();
      routeRoot?.removeEventListener("load", restoreScroll, true);
      window.removeEventListener("wheel", stopRestoring);
      window.removeEventListener("touchstart", stopRestoring);
      window.removeEventListener("pointerdown", stopRestoring);
      window.removeEventListener("keydown", stopRestoring);
    };
  }, [
    activePersistentRoute,
    activePersistentRouteMounted,
    getWindowScrollTop,
  ]);

  useEffect(() => {
    if (!["documents", "math-bank"].includes(activePersistentRoute)) {
      return undefined;
    }

    const handlePersistentTabSwitch = (event) => {
      if (
        event.key !== "Tab" ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        window.__SUNNY_DISABLE_PERSISTENT_TAB_SWITCH__
      ) {
        return;
      }

      event.preventDefault();
      capturePersistentScroll(activePersistentRoute);
      navigate(activePersistentRoute === "documents" ? "/math-bank" : "/documents");
    };

    window.addEventListener("keydown", handlePersistentTabSwitch);
    return () => window.removeEventListener("keydown", handlePersistentTabSwitch);
  }, [activePersistentRoute, capturePersistentScroll, navigate]);

  return (
    <div className="container">
      <AuthenticatedToolbar>
        <StyledNavWrapper>
          <IconButtonLeft
            type="button"
            aria-label="向左滑動選單"
            onClick={handleScrollLeft}
          >
            <FaChevronLeft />
          </IconButtonLeft>
          <StyledNav ref={navRef}>
            {menuItems.map((item) => (
              <StyledLink
                key={item.path}
                to={item.path}
                onClick={() => capturePersistentScroll(activePersistentRoute)}
              >
                {item.label}
              </StyledLink>
            ))}
          </StyledNav>
          <IconButtonRight
            type="button"
            aria-label="向右滑動選單"
            onClick={handleScrollRight}
          >
            <FaChevronRight />
          </IconButtonRight>
        </StyledNavWrapper>
        <AuthThemeToggle authTheme={authTheme} onToggleAuthTheme={onToggleAuthTheme} />
      </AuthenticatedToolbar>

      <Routes>
        <Route index element={<Navigate to="board" replace />} />
        <Route
          path="accounting"
          element={null}
        />
        <Route
          path="clock"
          element={null}
        />
        <Route path="board" element={null} />
        <Route path="documents" element={null} />
        <Route path="math-bank" element={null} />
        <Route path="math-bank-picker" element={null} />
        <Route path="upload" element={null} />
        <Route path="notes" element={<NotePage data={noteData} onSubmit={setNoteData} />} />
        <Route path="todo" element={<TodoPage data={todoData} onSubmit={setTodoData} />} />
      </Routes>

      {mountedPersistentRoutes.accounting && (
        <div hidden={activePersistentRoute !== "accounting"} data-persistent-route="accounting">
          <AccountingPage data={accountingData} sendAccountingData={sendAccountingData} onSaved={onAccountingSaved} error={accountingError} onRetry={retryAccounting} />
        </div>
      )}

      {mountedPersistentRoutes.documents && (
        <div
          hidden={activePersistentRoute !== "documents"}
          data-persistent-route="documents"
        >
          <DocumentProcessingPage
            routeActive={activePersistentRoute === "documents"}
            onOpenQuestionBank={() => {
              capturePersistentScroll("documents");
              navigate("/math-bank");
            }}
          />
        </div>
      )}

      {mountedPersistentRoutes.board && (
        <div hidden={activePersistentRoute !== "board"} data-persistent-route="board">
          <BulletinPage />
        </div>
      )}

      {mountedPersistentRoutes.clock && (
        <div hidden={activePersistentRoute !== "clock"} data-persistent-route="clock">
          <PunchingPage fetchData={fetchAllWorkHours} onDelete={deleteWorkHourRecord} />
        </div>
      )}

      {mountedPersistentRoutes.upload && (
        <div hidden={activePersistentRoute !== "upload"} data-persistent-route="upload">
          <CloudflareFileManager />
        </div>
      )}

      {mountedPersistentRoutes["math-bank"] && (
        <div
          hidden={activePersistentRoute !== "math-bank"}
          data-persistent-route="math-bank"
        >
          <QuestionBankAdminPage
            routeActive={activePersistentRoute === "math-bank"}
            editRequest={questionEditRequest}
            onEditRequestHandled={handleQuestionEditRequestHandled}
          />
        </div>
      )}

      {mountedPersistentRoutes["math-bank-picker"] && (
        <div
          hidden={activePersistentRoute !== "math-bank-picker"}
          data-persistent-route="math-bank-picker"
        >
          <QuestionBankPickerPage
            routeActive={activePersistentRoute === "math-bank-picker"}
            onEditQuestion={(question, subject) => {
              questionEditSequenceRef.current += 1;
              setQuestionEditRequest({
                id: question.id || question.uuid,
                subject,
                token: questionEditSequenceRef.current,
              });
              capturePersistentScroll("math-bank-picker");
              navigate("/math-bank");
            }}
          />
        </div>
      )}

      {(location.pathname.includes("notes") || location.pathname.includes("todo")) && (
        <CenteredContainer>
          <HistoryButtonGroup>
            <StyledButton type="button" $variant="secondary" onClick={() => onHistoryJump("undo")}>
              <FaUndo />
              往前跳
            </StyledButton>
            <StyledButton type="button" onClick={onSubmit}>
              <FaSave />
              儲存目前版本
            </StyledButton>
            <StyledButton type="button" $variant="secondary" onClick={() => onHistoryJump("redo")}>
              <FaRedo />
              往後跳
            </StyledButton>
          </HistoryButtonGroup>
        </CenteredContainer>
      )}
    </div>
  );
};

export default AuthenticatedView;

